import { useMemo, useState } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { CalendarDays, ChevronLeft, ChevronRight, ClipboardCheck, Loader2, Pencil, Plus, Search, Upload } from 'lucide-react';
import {
  useFazendaTalhoes,
  useSincronizarFazenda,
  useVisitasFazenda,
  useVisitasPendentes,
} from '../api';
import { isNative } from '@/lib/native-auth';
import { Select } from '@/ui/primitives';
import type { TalhaoResumo } from '../schemas';
import { CalendarioModal } from './CalendarioRelatorio';
import { OfflineMap } from './OfflineMap';
import { SeverityChip } from './SeverityChip';

function fmtHa(ha: number | null | undefined): string {
  if (ha == null) return '—';
  return `${ha.toLocaleString('pt-BR', { maximumFractionDigits: 0 })} ha`;
}
// Tela 03 · Fazenda (detalhe) — mapa, clima, talhões (mobile).
// Tela 16 · Fazenda (desktop/web): ≥lg vira 2 colunas — esquerda 380px
// (relatório, revisar, clima, talhões) e mapa preenchendo a direita.
// Sync e FAB "Nova visita" são fluxos de campo → só mobile.
export function FazendaDetailView({ imovelId }: { imovelId: number }) {
  const navigate = useNavigate();
  const { data: talhoes, isLoading } = useFazendaTalhoes(imovelId);
  const { data: pendentes } = useVisitasPendentes(imovelId);
  const { data: visitas } = useVisitasFazenda(imovelId);
  const sincronizar = useSincronizarFazenda(imovelId);
  const nPend = (pendentes ?? []).length;
  const aRevisar = (visitas ?? []).filter((v) => !v.revisado).length;

  const primeiro = (talhoes ?? [])[0];
  const fazenda = primeiro?.fazenda ?? 'Fazenda';
  const local = [primeiro?.municipio, primeiro?.uf].filter(Boolean).join('/');
  const totalHa = (talhoes ?? []).reduce((s, t) => s + (t.area_ha ?? 0), 0);
  const n = (talhoes ?? []).length;

  // Modal do calendário de relatório (desktop).
  const [relatorioOpen, setRelatorioOpen] = useState(false);

  // Select de talhão é atalho de CAMPO: só no APK. Na web fica a lista de cards.
  const appNativo = isNative();

  // Busca de talhão (propriedades grandes): filtra por nome, número ou cultura.
  const [buscaTalhao, setBuscaTalhao] = useState('');
  const talhoesFiltrados = useMemo(() => {
    const q = buscaTalhao.trim().toLowerCase();
    if (!q) return talhoes ?? [];
    return (talhoes ?? []).filter(
      (t) =>
        (t.talhao_nome ?? '').toLowerCase().includes(q) ||
        t.numero.toLowerCase().includes(q) ||
        `talhão ${t.numero}`.toLowerCase().includes(q) ||
        (t.cultura_atual ?? '').toLowerCase().includes(q),
    );
  }, [talhoes, buscaTalhao]);

  return (
    <div className="relative flex h-full flex-col bg-bg">
      {/* header mobile — no desktop o título vive na coluna esquerda,
          liberando a coluna do mapa pra preencher a altura inteira */}
      <header className="flex shrink-0 flex-col px-3 pt-4 pb-3 lg:hidden">
        <div className="flex items-center gap-1">
          <button
            type="button"
            onClick={() => window.history.back()}
            className="-ml-1 flex size-9 shrink-0 items-center justify-center rounded-full text-text hover:bg-surface2"
            aria-label="Voltar"
          >
            <ChevronLeft size={26} strokeWidth={2} />
          </button>
          <h1 className="min-w-0 flex-1 truncate text-[1.35rem] font-bold leading-tight tracking-tight text-text">
            {fazenda}
          </h1>
          <button
            type="button"
            onClick={() => void navigate({ to: '/agronomia/fazendas/$id/editar', params: { id: String(imovelId) } })}
            className="flex size-9 shrink-0 items-center justify-center rounded-full text-text-2 hover:bg-surface2 hover:text-text"
            aria-label="Editar fazenda"
          >
            <Pencil size={19} strokeWidth={1.75} />
          </button>
        </div>
        <span className="pl-9 text-[0.85rem] text-text-2">
          {[local, fmtHa(totalHa), `${n} ${n === 1 ? 'talhão' : 'talhões'}`].filter(Boolean).join(' · ')}
        </span>
      </header>

      <div className="min-h-0 flex-1 overflow-y-auto pb-40 lg:overflow-hidden lg:pb-0">
        <div className="lg:grid lg:h-full lg:grid-cols-[380px_minmax(0,1fr)] lg:gap-6 lg:px-8 lg:pb-8 lg:pt-7">
          {/* mapa da fazenda (Leaflet no cliente: pins + camadas + rota + tela cheia,
              online e offline; satélite do Google ao vivo ou do cache).
              Mobile: bloco no topo. Desktop: coluna direita inteira. */}
          <div className="mx-4 mb-4 h-[473px] overflow-hidden rounded-2xl bg-surface2 lg:order-2 lg:m-0 lg:h-full lg:min-h-0 lg:rounded-[14px]">
            <OfflineMap imovelId={imovelId} className="h-full w-full object-cover" />
          </div>

          {/* coluna esquerda (desktop) / restante da página (mobile). A ordem
              muda por breakpoint via `order-*`: desktop põe o botão de
              relatório no topo, como na tela 16. */}
          <div className="scroll-auto-hide flex flex-col lg:order-1 lg:h-full lg:min-h-0 lg:gap-[18px] lg:overflow-y-auto lg:pr-2.5">
            {/* título — desktop: primeiro bloco da coluna esquerda */}
            <div className="hidden lg:order-first lg:block">
              <div className="flex items-center gap-1.5">
                <button
                  type="button"
                  onClick={() => window.history.back()}
                  className="flex size-8 shrink-0 items-center justify-center rounded-full text-text-2 hover:bg-surface2 hover:text-text"
                  aria-label="Voltar"
                >
                  <ChevronLeft size={22} strokeWidth={2} />
                </button>
                <h1 className="min-w-0 truncate text-xl font-semibold leading-7 text-text">{fazenda}</h1>
                <button
                  type="button"
                  onClick={() => void navigate({ to: '/agronomia/fazendas/$id/editar', params: { id: String(imovelId) } })}
                  className="flex size-8 shrink-0 items-center justify-center rounded-full text-text-2 hover:bg-surface2 hover:text-text"
                  aria-label="Editar fazenda"
                >
                  <Pencil size={17} strokeWidth={1.75} />
                </button>
              </div>
              <span className="pl-[38px] text-sm text-text-2">
                {[local, fmtHa(totalHa), `${n} ${n === 1 ? 'talhão' : 'talhões'}`].filter(Boolean).join(' · ')}
              </span>
            </div>

            {/* sincronizar — fluxo de campo, só mobile */}
            <div className="order-1 px-4 lg:hidden">
              <div className="flex items-center gap-2.5 rounded-[14px] border border-[var(--agro-card-border)] bg-surface2 px-3 py-2.5">
                <span className="flex size-10 shrink-0 items-center justify-center rounded-[11px] bg-[var(--accent-100)]">
                  <Upload size={20} strokeWidth={1.75} className="text-accent" />
                </span>
                <div className="flex min-w-0 flex-1 flex-col">
                  <span className="text-[0.9rem] font-semibold text-text">Visitas para sincronizar</span>
                  <span className="text-[0.8rem] text-text-2">
                    {nPend > 0 ? `${nPend} ${nPend === 1 ? 'salva' : 'salvas'} no dispositivo` : 'Tudo sincronizado'}
                  </span>
                </div>
                <button
                  type="button"
                  disabled={nPend === 0 || sincronizar.isPending}
                  onClick={() => sincronizar.mutate()}
                  className="inline-flex items-center gap-1.5 rounded-full bg-[var(--accent)] px-3.5 py-2 text-[0.8rem] font-semibold text-[var(--on-solid)] disabled:opacity-50"
                >
                  {sincronizar.isPending ? <Loader2 size={15} strokeWidth={2} className="animate-spin" /> : <Upload size={15} strokeWidth={2} />}
                  Sincronizar
                </button>
              </div>
            </div>

            {/* gerar relatório consolidado — desktop: primeiro item da coluna.
                Desktop abre o calendário num modal sobre a tela; mobile navega
                pra página cheia (fica ótima no celular). */}
            <div className="order-4 px-4 pt-4 lg:order-first lg:px-0 lg:pt-0">
              <button
                type="button"
                onClick={() => {
                  if (typeof window !== 'undefined' && window.matchMedia('(min-width: 1024px)').matches) {
                    setRelatorioOpen(true);
                  } else {
                    void navigate({ to: '/agronomia/fazendas/$id/relatorio', params: { id: String(imovelId) } });
                  }
                }}
                className="flex w-full items-center justify-center gap-2.5 rounded-full border-[1.4px] border-[#bdb9b4] py-3 text-[0.9rem] font-medium text-text hover:bg-surface2"
              >
                <CalendarDays size={18} strokeWidth={1.75} /> Gerar relatório consolidado
              </button>
            </div>

            {/* revisar visitas */}
            <div className="order-2 px-4 pt-2.5 lg:order-none lg:px-0 lg:pt-0">
              <button
                type="button"
                onClick={() => void navigate({ to: '/agronomia/fazendas/$id/revisar', params: { id: String(imovelId) } })}
                className="flex w-full items-center gap-2.5 rounded-[14px] border border-[var(--agro-card-border)] bg-surface2 px-3 py-2.5 text-left active:scale-[0.99]"
              >
                <span className="flex size-10 shrink-0 items-center justify-center rounded-[11px] bg-[var(--accent-100)]">
                  <ClipboardCheck size={20} strokeWidth={1.75} className="text-accent" />
                </span>
                <div className="flex min-w-0 flex-1 flex-col">
                  <span className="text-[0.9rem] font-semibold text-text">Revisar visitas</span>
                  <span className="text-[0.8rem] text-text-2">
                    {aRevisar > 0 ? `${aRevisar} ${aRevisar === 1 ? 'visita' : 'visitas'} aguardando revisão` : 'Nenhuma aguardando revisão'}
                  </span>
                </div>
                <ChevronRight size={18} strokeWidth={2} className="shrink-0 text-[#565350]" />
              </button>
            </div>

            {/* talhões */}
            <div className="order-5 lg:order-none">
              <p className="px-5 pb-2 pt-5 text-[0.72rem] font-semibold uppercase tracking-wider text-accent lg:px-0 lg:pt-0 lg:text-[11px] lg:font-medium lg:tracking-[0.5px]">
                Talhões
              </p>
              {/* No APK: só o select, que abre o talhão direto — a lista rolável
                  atrapalha em campo. Na web segue a busca + a lista de cards. */}
              {appNativo ? (
                n > 0 && (
                  <div className="px-4 pb-2.5">
                    <Select
                      aria-label="Ir para o talhão"
                      value=""
                      onChange={(e) => {
                        const id = e.target.value;
                        if (id) void navigate({ to: '/agronomia/talhoes/$id', params: { id } });
                      }}
                      className="rounded-full border-[var(--agro-card-border)] bg-surface2"
                    >
                      <option value="">Ir para o talhão…</option>
                      {(talhoes ?? []).map((t) => (
                        <option key={t.talhao_id} value={String(t.talhao_id)}>
                          {t.talhao_nome || `Talhão ${t.numero}`}
                          {t.cultura_atual ? ` · ${t.cultura_atual}` : ''}
                        </option>
                      ))}
                    </Select>
                  </div>
                )
              ) : (
                <>
                  {/* busca — propriedades grandes: acha o talhão por nome/número/cultura */}
                  {n > 0 && (
                    <div className="relative px-4 pb-2.5 lg:px-0">
                      <Search
                        size={16}
                        strokeWidth={1.75}
                        className="pointer-events-none absolute left-7 top-1/2 -translate-y-[calc(50%+5px)] text-text-muted lg:left-3.5"
                      />
                      <input
                        value={buscaTalhao}
                        onChange={(e) => setBuscaTalhao(e.target.value)}
                        placeholder="Buscar talhão…"
                        className="w-full rounded-full border border-[var(--agro-card-border)] bg-surface2 py-2.5 pl-10 pr-4 text-[0.9rem] text-text placeholder:text-text-muted focus:border-[var(--accent)] focus:outline-none"
                      />
                    </div>
                  )}
                  {!isLoading && n > 0 && talhoesFiltrados.length === 0 && (
                    <p className="py-6 text-center text-sm text-text-muted">Nenhum talhão encontrado pra “{buscaTalhao.trim()}”.</p>
                  )}
                  <div className="flex flex-col gap-2.5 px-4 lg:gap-2 lg:px-0">
                    {talhoesFiltrados.map((t) => (
                      <TalhaoRow
                        key={t.talhao_id}
                        t={t}
                        onClick={() => void navigate({ to: '/agronomia/talhoes/$id', params: { id: String(t.talhao_id) } })}
                      />
                    ))}
                  </div>
                </>
              )}
              {isLoading && <p className="py-6 text-center text-sm text-text-muted">Carregando…</p>}
            </div>
          </div>
        </div>
      </div>

      <button
        type="button"
        onClick={() => void navigate({ to: '/agronomia/fazendas/$id/visita', params: { id: String(imovelId) } })}
        className="absolute bottom-24 right-4 z-10 inline-flex items-center gap-2 rounded-full bg-[var(--accent)] px-5 py-3.5 text-[0.92rem] font-semibold text-[var(--on-solid)] shadow-[var(--shadow-over)] lg:hidden"
      >
        <Plus size={20} strokeWidth={2.25} /> Nova visita técnica
      </button>

      {relatorioOpen && (
        <CalendarioModal imovelId={imovelId} fazenda={fazenda} onClose={() => setRelatorioOpen(false)} />
      )}
    </div>
  );
}

function TalhaoRow({ t, onClick }: { t: TalhaoResumo; onClick: () => void }) {
  const sub = [t.cultura_atual, t.safra_atual, fmtHa(t.area_ha)].filter(Boolean).join(' · ');
  return (
    <button
      type="button"
      onClick={onClick}
      className="flex w-full items-center gap-3 rounded-[12px] border border-[var(--agro-card-border)] bg-surface2 py-2.5 pl-2.5 pr-3 text-left active:scale-[0.99]"
    >
      <span className="flex size-10 shrink-0 items-center justify-center rounded-[8px] bg-[#dcefdf] text-[0.82rem] font-medium text-[#1d5a33]">
        {`T${t.numero}`.slice(0, 3)}
      </span>
      <div className="flex min-w-0 flex-1 flex-col gap-0.5">
        <span className="truncate text-[0.9rem] font-medium text-text">{t.talhao_nome || `Talhão ${t.numero}`}</span>
        <span className="text-[0.8rem] text-text-2">{sub || '—'}</span>
      </div>
      <SeverityChip severity="ok" />
      <ChevronRight size={16} strokeWidth={2} className="shrink-0 text-[#565350]" />
    </button>
  );
}
